import { Calendar as CalendarIcon } from 'iconoir-react'
import moment, { Moment } from 'moment'
import { useEffect, useState, useRef, useMemo } from 'react'
import { ChevronToggle } from 'app/components/ChevronToggle'
import CalendarRangePicker from 'app/components/marketing/CalendarRangePicker'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import {
  DateRangeOperator,
  FilterableColumn,
  FilterObject,
  FilterOperator,
  getDateRangeValues,
  isIndefiniteDatePreset,
} from 'app/filter.utils'

const selectorPresetOptions = [
  { value: DateRangeOperator.custom, display: `Custom Date` },
  {
    value: DateRangeOperator.today,
    display: `Today`,
  },
  {
    value: DateRangeOperator.yesterday,
    display: `Yesterday`,
  },
  {
    value: DateRangeOperator.this_week,
    display: `This Week`,
  },
  {
    value: DateRangeOperator.last_week,
    display: `Last Week`,
  },
  {
    value: DateRangeOperator.this_month,
    display: `This Month`,
  },
  {
    value: DateRangeOperator.last_month,
    display: `Last Month`,
  },
  {
    value: DateRangeOperator.this_year,
    display: `This Year`,
  },
  {
    value: DateRangeOperator.last_year,
    display: `Last Year`,
  },
  {
    value: DateRangeOperator.last_30_days,
    display: `Last 30 Days`,
  },
  {
    value: DateRangeOperator.last_90_days,
    display: `Last 90 Days`,
  },
  {
    value: DateRangeOperator.last_6_months,
    display: `Last 6 months`,
  },
  {
    value: DateRangeOperator.last_12_months,
    display: `Last 12 months`,
  },
  {
    value: DateRangeOperator.all,
    display: `All`,
  },
  {
    value: DateRangeOperator.in_the_past,
    display: `In the past`,
  },
  {
    value: DateRangeOperator.next_30_days,
    display: `Next 30 Days`,
  },
  {
    value: DateRangeOperator.next_90_days,
    display: `Next 90 Days`,
  },
  {
    value: DateRangeOperator.today_or_later,
    display: `Today or later`,
  },
  {
    value: DateRangeOperator.in_the_future,
    display: `In the future`,
  },
]

const futurePresets = [
  DateRangeOperator.next_30_days,
  DateRangeOperator.next_90_days,
  DateRangeOperator.today_or_later,
  DateRangeOperator.in_the_future,
]

const getInitialSelectorPreset = (filter: FilterObject, limitToMonth: boolean) => {
  if (Array.isArray(filter.value)) {
    return inferSelectorPreset(
      moment(moment(filter.value[0]).format(limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`)),
      moment(moment(filter.value[1]).format(limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`)),
      limitToMonth,
    )
  }

  if (typeof filter.value === `string`) {
    const preset = DateRangeOperator[filter.value as keyof typeof DateRangeOperator]
    if (typeof preset === `number`) return preset
  }

  if (typeof filter.defaultValue === `string`) {
    const preset = DateRangeOperator[filter.defaultValue as keyof typeof DateRangeOperator]
    if (typeof preset === `number`) return preset
  }

  return DateRangeOperator.all
}

const getInitialDateRange = (filter: FilterObject, limitToMonth: boolean): { fromDate: Moment; toDate: Moment } => {
  if (Array.isArray(filter.value) && filter.value.at(0) && moment(filter.value.at(1)).isValid()) {
    return {
      fromDate: moment(moment(filter.value[0]).format(limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`)),
      toDate: moment(moment(filter.value[1]).format(limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`)),
    }
  }

  if (filter.value === `*`) {
    return {
      fromDate: moment(`1900-01-01`, limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`),
      toDate: moment(),
    }
  }

  const fallbackValue = typeof filter.defaultValue === `string` ? filter.defaultValue : undefined
  const presetValue = typeof filter.value === `string` ? filter.value : fallbackValue
  if (presetValue && !isIndefiniteDatePreset(presetValue)) {
    const preset = DateRangeOperator[presetValue as keyof typeof DateRangeOperator]
    if (typeof preset === `number`) {
      const [fromDate, toDate] = getDateRangeValues(preset)
      return { fromDate, toDate }
    }
  }

  return {
    fromDate: moment(),
    toDate: moment(),
  }
}

const inferSelectorPreset = (fromDate: moment.Moment, toDate: moment.Moment, limitToMonth: boolean) => {
  let granularity = (limitToMonth ? `month` : `day`) as moment.unitOfTime.StartOf
  if (
    fromDate.isSame(moment().subtract(1, `day`).startOf(`day`), `day`) &&
    toDate.isSame(moment().subtract(1, `day`).endOf(`day`), `day`)
  ) {
    return DateRangeOperator.yesterday
  } else if (fromDate.isSame(moment().startOf(`day`), `day`) && toDate.isSame(moment().endOf(`day`), `day`)) {
    return DateRangeOperator.today
  } else if (
    fromDate.isSame(moment().startOf(`month`), granularity) &&
    toDate.isSame(moment().endOf(`month`), granularity)
  ) {
    return DateRangeOperator.this_month
  } else if (
    fromDate.isSame(moment().subtract(1, `month`).startOf(`month`), granularity) &&
    toDate.isSame(moment().subtract(1, `month`).endOf(`month`), granularity)
  ) {
    return DateRangeOperator.last_month
  } else if (
    fromDate.isSame(moment().startOf(`day`), granularity) &&
    toDate.isSame(moment().add(30, `days`).endOf(`day`), granularity)
  ) {
    return DateRangeOperator.next_30_days
  } else if (
    fromDate.isSame(moment().startOf(`day`), granularity) &&
    toDate.isSame(moment().add(90, `days`).endOf(`day`), granularity)
  ) {
    return DateRangeOperator.next_90_days
  } else if (fromDate.isSame(moment().startOf(`week`), `day`) && toDate.isSame(moment().endOf(`week`), `day`)) {
    return DateRangeOperator.this_week
  } else if (
    fromDate.isSame(moment().subtract(1, `week`).startOf(`week`), `day`) &&
    toDate.isSame(moment().subtract(1, `week`).endOf(`week`), `day`)
  ) {
    return DateRangeOperator.last_week
  } else if (fromDate.isSame(moment().startOf(`year`), granularity) && toDate.isSame(moment(), granularity)) {
    return DateRangeOperator.this_year
  } else if (
    fromDate.isSame(moment().subtract(1, `year`).startOf(`year`), granularity) &&
    toDate.isSame(moment().subtract(1, `year`).endOf(`year`), granularity)
  ) {
    return DateRangeOperator.last_year
  } else if (
    fromDate.isSame(moment().subtract(30, `days`).startOf(`day`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_30_days
  } else if (
    fromDate.isSame(moment().subtract(90, `days`).startOf(`day`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_90_days
  } else if (
    fromDate.isSame(moment().subtract(6, `months`).startOf(`month`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_6_months
  } else if (
    fromDate.isSame(moment().subtract(12, `months`).startOf(`month`), granularity) &&
    toDate.isSame(moment(), granularity)
  ) {
    return DateRangeOperator.last_12_months
  } else {
    return DateRangeOperator.custom
  }
}

export default function DateRangeFilter({
  filter,
  column,
  limitToMonth = false,
  includeAll = true,
  includeYesterday = false,
  includeToday = false,
  includeWeeks = true,
  includeInThePast = false,
  includeFuture = false,
  onEdit,
}: {
  filter: FilterObject
  column: FilterableColumn
  limitToMonth?: boolean
  includeAll?: boolean
  includeYesterday?: boolean
  includeToday?: boolean
  includeWeeks?: boolean
  includeInThePast?: boolean
  includeFuture?: boolean
  onEdit: (val: string | string[] | undefined, operator: FilterOperator) => void
  onRemove: (filter: FilterObject) => void
}) {
  const didMount = useRef(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [selectorPreset, setSelectorPreset] = useState<DateRangeOperator>(() =>
    getInitialSelectorPreset(filter, limitToMonth),
  )
  const [openFromToDatePicker, setOpenFromToDatePicker] = useState<boolean>(false)
  const [fromDate, setFromDate] = useState<Moment>(() => getInitialDateRange(filter, limitToMonth).fromDate)
  const [toDate, setToDate] = useState<Moment>(() => getInitialDateRange(filter, limitToMonth).toDate)

  const updateDateRange = (fromDate: Moment, toDate: Moment, selectorPreset: DateRangeOperator) => {
    setFromDate(fromDate)
    setToDate(toDate)
    if (selectorPreset === DateRangeOperator.all) {
      onEdit(`*`, `between`)
    } else if (selectorPreset === DateRangeOperator.custom) {
      onEdit([fromDate.format(`YYYY-MM-DD`), toDate.format(`YYYY-MM-DD`)], `between`)
    } else onEdit(DateRangeOperator[selectorPreset], `between`)
  }

  const handleFromToDateChange = (dates: Date[]) => {
    setSelectorPreset(DateRangeOperator.custom)
    let fromDate = moment(dates[0])
    let toDate = moment(dates[1])
    if (limitToMonth) {
      fromDate = fromDate.startOf(`month`)
      toDate = toDate.endOf(`month`)
    }
    updateDateRange(fromDate, toDate, DateRangeOperator.custom)
  }

  let handleSelectorChange = (value: string) => {
    const selectorPreset = DateRangeOperator[value as keyof typeof DateRangeOperator] as DateRangeOperator
    let fromDateLocal: Moment | undefined = undefined
    let toDateLocal: Moment | undefined = undefined
    if (didMount.current) {
      switch (selectorPreset) {
        case DateRangeOperator.yesterday:
          fromDateLocal = moment().subtract(1, `day`).startOf(`day`)
          toDateLocal = moment().subtract(1, `day`).endOf(`day`)
          break
        case DateRangeOperator.today:
          fromDateLocal = moment().startOf(`day`)
          toDateLocal = moment()
          break
        case DateRangeOperator.this_week:
          fromDateLocal = moment().startOf(`week`)
          toDateLocal = moment()
          break
        case DateRangeOperator.last_week:
          fromDateLocal = moment().subtract(1, `week`).startOf(`week`)
          toDateLocal = moment().subtract(1, `week`).endOf(`week`)
          break
        case DateRangeOperator.this_month:
          fromDateLocal = moment().startOf(`month`)
          toDateLocal = moment().endOf(`month`)
          break
        case DateRangeOperator.last_month:
          fromDateLocal = moment().subtract(1, `month`).startOf(`month`)
          toDateLocal = moment().subtract(1, `month`).endOf(`month`)
          break
        case DateRangeOperator.last_30_days:
          fromDateLocal = moment().subtract(30, `days`).startOf(`day`)
          toDateLocal = moment()
          break
        case DateRangeOperator.last_90_days:
          fromDateLocal = moment().subtract(90, `days`).startOf(`day`)
          toDateLocal = moment()
          break
        case DateRangeOperator.next_30_days:
          fromDateLocal = moment().startOf(`day`)
          toDateLocal = moment().add(30, `days`).endOf(`day`)
          break
        case DateRangeOperator.next_90_days:
          fromDateLocal = moment().startOf(`day`)
          toDateLocal = moment().add(90, `days`).endOf(`day`)
          break
        case DateRangeOperator.last_6_months:
          fromDateLocal = moment().subtract(6, `months`).startOf(`month`)
          toDateLocal = moment()
          break
        case DateRangeOperator.last_12_months:
          fromDateLocal = moment().subtract(12, `months`).startOf(`month`)
          toDateLocal = moment()
          break
        case DateRangeOperator.this_year:
          fromDateLocal = moment().startOf(`year`)
          toDateLocal = moment()
          break
        case DateRangeOperator.last_year:
          fromDateLocal = moment().subtract(1, `year`).startOf(`year`)
          toDateLocal = moment().subtract(1, `year`).endOf(`year`)
          break
        case DateRangeOperator.custom:
          setOpenFromToDatePicker(true)
          break
        case DateRangeOperator.in_the_past:
        case DateRangeOperator.in_the_future:
        case DateRangeOperator.today_or_later:
          onEdit(DateRangeOperator[selectorPreset], `between`)
          setIsModalOpen(false)
          setSelectorPreset(selectorPreset)
          return
      }
    }
    if (fromDateLocal && toDateLocal) {
      updateDateRange(fromDateLocal, toDateLocal, selectorPreset)
    } else if (selectorPreset === DateRangeOperator.all) {
      setFromDate(moment(`1900-01-01`, limitToMonth ? `YYYY-MM` : `YYYY-MM-DD`))
      setToDate(moment())
      onEdit(`*`, `attribute_exists`)
    }
    if (selectorPreset !== DateRangeOperator.custom) setIsModalOpen(false)
    setSelectorPreset(selectorPreset)
  }

  // hack to define when a component mounts
  useEffect(() => {
    didMount.current = true
  }, [])

  let dateRange = useMemo(() => {
    let format = limitToMonth ? `MMMM YYYY` : `MM/DD/YY`
    return `${fromDate.format(format)} - ${toDate.format(format)}`
  }, [fromDate, toDate, limitToMonth])

  let getDateRange = () => {
    let selector = selectorPresetOptions.find((option) => option.value === selectorPreset)
    switch (selector!.display) {
      case `Custom Date`:
        return dateRange
      default:
        return selector!.display
    }
  }

  const filteredOptions = useMemo(
    () =>
      selectorPresetOptions.filter((option) => {
        if (!includeAll && option.value === DateRangeOperator.all) return false
        if (!includeToday && option.value === DateRangeOperator.today) return false
        if (!includeYesterday && option.value === DateRangeOperator.yesterday) return false
        if (!includeInThePast && option.value === DateRangeOperator.in_the_past) return false
        if (!includeFuture && futurePresets.includes(option.value)) return false
        if (
          !includeWeeks &&
          (option.value === DateRangeOperator.this_week || option.value === DateRangeOperator.last_week)
        )
          return false
        return true
      }),
    [includeAll, includeToday, includeYesterday, includeInThePast, includeFuture, includeWeeks],
  )

  return (
    <Popover open={isModalOpen} onOpenChange={setIsModalOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          x-component-name="TableFilters"
          className="relative flex h-8 w-60 items-center justify-between rounded-lg border border-default bg-surface-raised px-2 disabled:text-minimal disabled:opacity-70"
          aria-label={filter.displayName}
          disabled={column.readOnly}
        >
          <span className="text-body-sm capitalize leading-none shadow-elevation-1">{getDateRange()}</span>
          <div className="stuff-on-the-right absolute inset-y-0 right-0 flex items-center">
            <ChevronToggle open={isModalOpen} />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[350px] bg-surface-raised p-0 shadow-elevation-4 ring-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="w-full p-4">
          <div>
            <p className="text-body-sm text-strong">Date range</p>
            <Select value={DateRangeOperator[selectorPreset]} onValueChange={handleSelectorChange}>
              <SelectTrigger className="mt-1 w-full rounded-lg border-strong bg-surface-raised text-body-sm text-strong shadow-elevation-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface-raised text-strong shadow-elevation-4">
                {filteredOptions.map((option) => (
                  <SelectItem key={option.value} value={DateRangeOperator[option.value]}>
                    {option.display}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isIndefiniteDatePreset(DateRangeOperator[selectorPreset]) && (
              <div className="mt-4 flex justify-between">
                <Popover open={openFromToDatePicker} onOpenChange={setOpenFromToDatePicker}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex w-full rounded-lg border border-strong px-4 py-2.5 text-body-xs text-strong focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-strong"
                    >
                      <CalendarIcon className="h-4 w-4" />
                      <div className="flex grow justify-evenly">
                        <span>{fromDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                        <span>-</span>
                        <span>{toDate.format(limitToMonth ? `MMMM YYYY` : `MM/DD/YY`)}</span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="bottom"
                    align="start"
                    sideOffset={5}
                    className="h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <CalendarRangePicker
                      limitToMonth={limitToMonth}
                      allowFutureDates={includeFuture}
                      startDate={fromDate}
                      endDate={toDate}
                      onDateChange={handleFromToDateChange}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
